class BcCollectionCarousel {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-viewport]');
    this.track = root.querySelector('.bc-collection-rail__track');
    this.slides = Array.from(root.querySelectorAll('[data-slide]'));
    this.prevButton = root.querySelector('[data-prev]');
    this.nextButton = root.querySelector('[data-next]');
    this.dotsRoot = root.querySelector('[data-dots]');
    this.arrowsWrap = root.querySelector('.bc-collection-rail__arrows');
    this.pageCount = 1;

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);

    let _resizeTimer = 0;
    this._handleResizeThrottled = () => {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(this.handleResize, 150);
    };

    this.prevButton?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByPage(1));
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this._handleResizeThrottled);

    this.buildDots();
    this.updateUi();

    /* Card images can still be loading at DOMContentLoaded, so scrollWidth
       (and the pageCount it drives) can be measured too early. Re-check once
       everything has actually loaded. */
    if (document.readyState === 'complete') {
      this.handleResize();
    } else {
      window.addEventListener('load', () => this.handleResize(), { once: true });
    }
  }

  getPageWidth() {
    return this.viewport.clientWidth;
  }

  getMaxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  getCurrentPage() {
    const pageWidth = this.getPageWidth();
    if (!pageWidth) return 0;
    return Math.round(this.viewport.scrollLeft / pageWidth);
  }

  buildDots() {
    const pageWidth = this.getPageWidth();
    const maxScroll = this.getMaxScroll();
    /* Ignore sub-pixel overflow — treat it as nothing to scroll. */
    this.pageCount = pageWidth && maxScroll > 24 ? Math.max(1, Math.round(maxScroll / pageWidth) + 1) : 1;

    /* Dots add nothing over the arrows at 2 pages; arrows add nothing at 1. */
    this.dotsRoot?.classList.toggle('is-hidden', this.pageCount <= 2);
    this.arrowsWrap?.classList.toggle('is-hidden', this.pageCount <= 1);

    if (!this.dotsRoot) return;
    this.dotsRoot.innerHTML = '';

    for (let index = 0; index < this.pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bc-collection-rail__dot';
      dot.setAttribute('aria-label', `Go to page ${index + 1}`);
      dot.addEventListener('click', () => {
        this.viewport.scrollTo({
          left: this.getPageWidth() * index,
          behavior: 'smooth'
        });
      });
      this.dotsRoot.appendChild(dot);
    }
  }

  updateUi() {
    const currentPage = this.getCurrentPage();
    const maxScroll = this.getMaxScroll();
    const currentScroll = this.viewport.scrollLeft;

    this.prevButton?.toggleAttribute('disabled', currentScroll <= 4);
    this.nextButton?.toggleAttribute('disabled', currentScroll >= maxScroll - 4);

    if (!this.dotsRoot) return;
    Array.from(this.dotsRoot.children).forEach((dot, index) => {
      dot.classList.toggle('is-active', index === currentPage);
    });
  }

  scrollByPage(direction) {
    this.viewport.scrollBy({
      left: this.getPageWidth() * direction,
      behavior: 'smooth'
    });
  }

  handleScroll() {
    this.updateUi();
  }

  handleResize() {
    this.buildDots();
    this.updateUi();
  }
}

function initBcCollectionCarousels() {
  document.querySelectorAll('[data-bc-collection-carousel]').forEach((carousel) => {
    if (carousel.dataset.bcCollectionCarouselInit === 'true') return;
    carousel.dataset.bcCollectionCarouselInit = 'true';
    new BcCollectionCarousel(carousel);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBcCollectionCarousels);
} else {
  initBcCollectionCarousels();
}

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('[data-bc-collection-carousel]').forEach((carousel) => {
    carousel.dataset.bcCollectionCarouselInit = 'false';
  });
  initBcCollectionCarousels();
});
